import React, { useState, useEffect } from 'react';
import { axiosInstance as axios } from '../../context/AuthContext';
import { FaTimes, FaUsers, FaVideo, FaPhone, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import { API, PROFESSIONAL_TYPES } from './resourcesConstants';

const downloadICS = (icsContent, filename) => {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export function BookingModal({ professional, onClose, onSuccess }) {
  const [bookingType, setBookingType] = useState('individual');
  const [formData, setFormData] = useState({
    consultation_type: 'video',
    preferred_date: '',
    preferred_time: '',
    notes: ''
  });
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [externalEmails, setExternalEmails] = useState('');
  const [families, setFamilies] = useState([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);

  useEffect(() => { fetchFamilies(); }, []);

  useEffect(() => {
    if (selectedFamilyId && bookingType === 'group') {
      fetchFamilyMembers(selectedFamilyId);
    }
  }, [selectedFamilyId, bookingType]);

  const fetchFamilies = async () => {
    try {
      const response = await axios.get(`${API}/families`, { withCredentials: true });
      setFamilies(response.data);
      if (response.data.length > 0) {
        setSelectedFamilyId(response.data[0].family_id);
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    }
  };

  const fetchFamilyMembers = async (familyId) => {
    setLoadingMembers(true);
    try {
      const response = await axios.get(`${API}/families/${familyId}`, { withCredentials: true });
      setFamilyMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching family members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const toggleMemberSelection = (userId) => {
    setSelectedMembers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (bookingType === 'group') {
        const emails = externalEmails
          .split(/[,;\n]/)
          .map(addr => addr.trim())
          .filter(addr => addr && addr.includes('@'));

        const response = await axios.post(
          `${API}/consultations/group`,
          {
            professional_id: professional.professional_id,
            consultation_type: formData.consultation_type,
            preferred_date: formData.preferred_date,
            preferred_time: formData.preferred_time,
            notes: formData.notes,
            family_id: selectedFamilyId || null,
            invited_member_ids: selectedMembers,
            invited_emails: emails
          },
          { withCredentials: true }
        );

        if (response.data.ics_content) {
          const confirmed = window.confirm(
            `Group consultation booked! ${response.data.invited_count} invitation(s) will be sent.\n\nWould you like to download the calendar invite?`
          );
          if (confirmed) {
            downloadICS(response.data.ics_content, `consultation_${response.data.consultation.consultation_id}.ics`);
          }
        }

        onSuccess(response.data.consultation);
      } else {
        const response = await axios.post(
          `${API}/consultations`,
          {
            professional_id: professional.professional_id,
            ...formData
          },
          { withCredentials: true }
        );
        onSuccess(response.data);
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to book consultation');
    } finally {
      setSubmitting(false);
    }
  };

  const typeInfo = PROFESSIONAL_TYPES[professional.professional_type] || { label: 'Professional' };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-800">Book Consultation</h3>
              <p className="text-gray-600">{professional.name}</p>
              <p className="text-sm text-gray-500">{typeInfo.label}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FaTimes />
            </button>
          </div>

          {/* Booking Type Toggle */}
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">Booking Type</label>
            <div className="flex rounded-lg overflow-hidden border border-gray-300">
              <button
                type="button"
                onClick={() => setBookingType('individual')}
                className={`flex-1 py-2 px-4 text-sm font-medium ${
                  bookingType === 'individual'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="individual-booking-btn"
              >
                Just Me
              </button>
              <button
                type="button"
                onClick={() => setBookingType('group')}
                className={`flex-1 py-2 px-4 text-sm font-medium ${
                  bookingType === 'group'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="group-booking-btn"
              >
                <FaUsers className="inline mr-2" />
                Group Session
              </button>
            </div>
            {bookingType === 'group' && (
              <p className="text-xs text-emerald-600 mt-1">
                Invite family members to participate together in this consultation
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Session Format */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Session Format</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'video', label: 'Video Call', icon: <FaVideo /> },
                  { value: 'phone', label: 'Phone', icon: <FaPhone /> },
                  { value: 'in_person', label: 'In Person', icon: <FaMapMarkerAlt /> }
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, consultation_type: option.value })}
                    className={`p-3 rounded-lg border text-center ${
                      formData.consultation_type === option.value
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-xl mb-1 flex justify-center">{option.icon}</div>
                    <div className="text-xs">{option.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Group Options */}
            {bookingType === 'group' && (
              <>
                {families.length > 0 && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Select Family</label>
                    <select
                      value={selectedFamilyId}
                      onChange={(e) => setSelectedFamilyId(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600"
                      data-testid="family-select"
                    >
                      {families.map(fam => (
                        <option key={fam.family_id} value={fam.family_id}>{fam.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Invite Family Members
                    <span className="text-sm text-gray-500 ml-2">({selectedMembers.length} selected)</span>
                  </label>
                  {loadingMembers ? (
                    <div className="text-center py-4 text-gray-500">Loading members...</div>
                  ) : familyMembers.length === 0 ? (
                    <p className="text-gray-500 text-sm">No family members found</p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                      {familyMembers.map(member => (
                        <label
                          key={member.user_id}
                          className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                            selectedMembers.includes(member.user_id) ? 'bg-emerald-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedMembers.includes(member.user_id)}
                            onChange={() => toggleMemberSelection(member.user_id)}
                            className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                          />
                          <span className="ml-3 text-gray-700">{member.name}</span>
                          {member.role && (
                            <span className="ml-auto text-xs text-gray-500 capitalize">{member.role}</span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Invite Others by Email
                    <span className="text-xs text-gray-500 ml-2">(optional)</span>
                  </label>
                  <textarea
                    value={externalEmails}
                    onChange={(e) => setExternalEmails(e.target.value)}
                    placeholder="Enter email addresses (one per line or comma-separated)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600 text-sm"
                    rows="2"
                    data-testid="external-emails-input"
                  />
                </div>
              </>
            )}

            {/* Date */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Preferred Date</label>
              <input
                type="date"
                value={formData.preferred_date}
                onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                required
                data-testid="consultation-date"
              />
            </div>

            {/* Time */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Preferred Time</label>
              <select
                value={formData.preferred_time}
                onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                required
                data-testid="consultation-time"
              >
                <option value="">Select a time</option>
                <option value="09:00">9:00 AM</option>
                <option value="10:00">10:00 AM</option>
                <option value="11:00">11:00 AM</option>
                <option value="13:00">1:00 PM</option>
                <option value="14:00">2:00 PM</option>
                <option value="15:00">3:00 PM</option>
                <option value="16:00">4:00 PM</option>
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Notes (Optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                rows="3"
                placeholder="Tell us what you'd like to discuss..."
                data-testid="consultation-notes"
              />
            </div>

            {/* Calendar Reminder Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <FaCalendarAlt className="text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">Calendar Reminder</p>
                  <p className="text-blue-600">
                    You'll receive a calendar invite (.ics file) that you can add to your calendar.
                    Reminders will be sent 1 day and 1 hour before the session.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                  bookingType === 'group'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
                data-testid="submit-booking-btn"
              >
                {submitting
                  ? 'Booking...'
                  : bookingType === 'group'
                    ? `Book Group Session ${selectedMembers.length > 0 ? `(${selectedMembers.length + 1} attendees)` : ''}`
                    : 'Request Consultation'
                }
              </button>
            </div>
          </form>

          <p className="text-xs text-gray-500 text-center mt-4">
            {bookingType === 'group'
              ? 'All invited members will receive an email notification with calendar invite.'
              : 'Initial consultations are free. You\'ll receive a confirmation email once your request is reviewed.'
            }
          </p>
        </div>
      </div>
    </div>
  );
}
